import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { handleApiError } from 'src/app/helper/helper';
import { ScanMinimal } from 'src/app/interfaces/scan.interfaces';
import { ApiService } from 'src/app/services/api.service';

export enum SortBy {
  ID,
  POSITION,
  OBJECTS,
  UPDATED_AT,
  UPDATED_BY,
  CREATED_AT,
  CREATED_BY,
}

@Component({
  standalone: false,
  selector: 'app-scan-list',
  templateUrl: './scan-list.component.html',
})
export class ScanListComponent implements OnInit {
  isLoaded = false;
  error: any = null;
  scans: ScanMinimal[] = [];
  page: ScanMinimal[] = [];

  pageSize = 25;
  pageCurrent = 1;
  pageMax = 1;
  pageSort = '';

  SortBy = SortBy;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadScans();
  }

  async loadScans() {
    try {
      this.scans = await this.apiService.fetchScans();
      this.isLoaded = true;
      this.updatePagination();
    } catch (error) {
      handleApiError(this.authService, this.router, error);
      this.error = error;
    }
  }

  updatePagination() {
    this.pageMax = Math.ceil(this.scans.length / this.pageSize);
    if (this.pageCurrent > this.pageMax) {
      this.pageCurrent = this.pageMax || 1;
    }
    this.switchToPage(this.pageCurrent);
  }

  switchToPage(pageNumber: number) {
    if (pageNumber >= 1 && (pageNumber <= this.pageMax || this.pageMax === 0)) {
      this.pageCurrent = pageNumber;
      const start = (pageNumber - 1) * this.pageSize;
      this.page = this.scans.slice(start, start + this.pageSize);
    }
  }

  onFirstPage() {
    this.switchToPage(1);
  }

  onPreviousPage() {
    this.switchToPage(this.pageCurrent - 1);
  }

  onNextPage() {
    this.switchToPage(this.pageCurrent + 1);
  }

  onLastPage() {
    this.switchToPage(this.pageMax);
  }

  onPageSizeChange() {
    this.updatePagination();
  }

  sortPage(sortBy: SortBy) {
    let compare = (a: ScanMinimal, b: ScanMinimal) => a.id - b.id;
    let newPageSort = '';

    switch (sortBy) {
      case SortBy.ID:
        if (this.pageSort === 'ID_ASC') {
          compare = (a, b) => b.id - a.id;
          newPageSort = 'ID_DESC';
        } else {
          compare = (a, b) => a.id - b.id;
          newPageSort = 'ID_ASC';
        }
        break;
      case SortBy.POSITION:
        if (this.pageSort === 'POSITION_X_ASC') {
          compare = (a, b) => (b.x - a.x === 0 ? b.y - a.y : b.x - a.x);
          newPageSort = 'POSITION_X_DESC';
        } else if (this.pageSort === 'POSITION_X_DESC') {
          compare = (a, b) => (a.y - b.y === 0 ? a.x - b.x : a.y - b.y);
          newPageSort = 'POSITION_Y_ASC';
        } else if (this.pageSort === 'POSITION_Y_ASC') {
          compare = (a, b) => (b.y - a.y === 0 ? b.x - a.x : b.y - a.y);
          newPageSort = 'POSITION_Y_DESC';
        } else {
          compare = (a, b) => (a.x - b.x === 0 ? a.y - b.y : a.x - b.x);
          newPageSort = 'POSITION_X_ASC';
        }
        break;
      case SortBy.OBJECTS:
        if (this.pageSort === 'OBJECTS_ASC') {
          compare = (a, b) => b.scanObjects - a.scanObjects;
          newPageSort = 'OBJECTS_DESC';
        } else {
          compare = (a, b) => a.scanObjects - b.scanObjects;
          newPageSort = 'OBJECTS_ASC';
        }
        break;
      case SortBy.UPDATED_AT:
        if (this.pageSort === 'UPDATED_AT_ASC') {
          compare = (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          newPageSort = 'UPDATED_AT_DESC';
        } else {
          compare = (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          newPageSort = 'UPDATED_AT_ASC';
        }
        break;
      case SortBy.UPDATED_BY:
        if (this.pageSort === 'UPDATED_BY_ASC') {
          compare = (a, b) => b.updatedBy.id - a.updatedBy.id;
          newPageSort = 'UPDATED_BY_DESC';
        } else {
          compare = (a, b) => a.updatedBy.id - b.updatedBy.id;
          newPageSort = 'UPDATED_BY_ASC';
        }
        break;
      case SortBy.CREATED_AT:
        if (this.pageSort === 'CREATED_AT_ASC') {
          compare = (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          newPageSort = 'CREATED_AT_DESC';
        } else {
          compare = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          newPageSort = 'CREATED_AT_ASC';
        }
        break;
      case SortBy.CREATED_BY:
        if (this.pageSort === 'CREATED_BY_ASC') {
          compare = (a, b) => b.createdBy.id - a.createdBy.id;
          newPageSort = 'CREATED_BY_DESC';
        } else {
          compare = (a, b) => a.createdBy.id - b.createdBy.id;
          newPageSort = 'CREATED_BY_ASC';
        }
        break;
    }

    this.scans.sort(compare);
    this.pageSort = newPageSort;
    this.switchToPage(1);
  }
}
